import React, { FC } from 'react';
import { Button as MaterialButton } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import i18n from '../../../i18n';
import { Text, TextTypography } from '../Text/Text';
import { Icon } from '../Icon/Icon';
import { ReactComponent as ChevronRightIcon } from 'assets/icons/main-16/chevron-right--16.svg';

export interface InfoButtonProps {
    icon: any;
    title: string;
    // Optional:
    info?: string;
    color?: string;
    onClick?: () => void;
    disabled?: boolean;
}

const useStyles = makeStyles(theme => ({
    containedPrimary: {
        backgroundColor: (props: { color: string }) =>
            props.color ? props.color : theme.palette.brandPrimary.lightest,
        borderRadius: 20,
        textTransform: 'none',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        '&:hover': {
            backgroundColor: theme.palette.brandPrimary.mainHover,
            '& span': {
                color: theme.palette.fixedWhite.primary,
            },
        },
        [theme.breakpoints.down(640)]: {
            padding: theme.spacing(2),
        },
    },
    icon: {
        display: 'flex',
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
    },
    iconSize: {
        width: 32,
        height: 32,
    },
    group: {
        display: 'flex',
        alignItems: 'center',
    },
    info: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        textAlign: 'initial',
    },
    additionalInfo: {
        whiteSpace: 'pre-line',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        '-webkit-line-clamp': 1,
        display: '-webkit-box',
        '-webkit-box-orient': 'vertical',
    },
    [theme.breakpoints.up('sm')]: {
        iconSize: {
            width: 48,
            height: 48,
        },
        containedPrimary: {
            height: 80,
            paddingLeft: 0,
            paddingRight: 0,
        },
    },
}));

export const InfoButton: FC<InfoButtonProps> = props => {
    const { icon, title, info, color, disabled, ...rest } = props;
    const classes = useStyles({ color });
    const { t } = i18n.useTranslation();
    return (
        <MaterialButton
            disabled={disabled ? disabled : false}
            className={classes.containedPrimary}
            variant="contained"
            disableElevation={true}
            fullWidth={true}
            {...rest}
        >
            <div className={classes.group}>
                <div className={classes.icon}>
                    <Icon className={classes.iconSize} component={icon} size={32} svgSize={32} />
                </div>
                <div className={classes.info}>
                    <Text typography={TextTypography.PARA_ACCENT}>{title}</Text>
                    {!!info && (
                        <Text className={classes.additionalInfo} typography={TextTypography.PARA}>
                            {info}
                        </Text>
                    )}
                </div>
            </div>
            <div className={classes.group}>
                {!info && <Text typography={TextTypography.PARA}>{t('common:infoButton.off')}</Text>}
                <div className={classes.icon}>
                    <Icon component={ChevronRightIcon} size={16} svgSize={16} />
                </div>
            </div>
        </MaterialButton>
    );
};
